export type PrintApiServiceConfig = {
  baseUrl: string;
  locationIdentifier: string;
};

type PrintApiEntity = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
};

export const PrintApiConnectionType = {
  usb: 'usb',
  lan: 'lan',
  wifi: 'wifi',
  bluetooth: 'bluetooth',
  unknown: 'unknown',
} as const;

export type PrintApiConnectionType = typeof PrintApiConnectionType[keyof typeof PrintApiConnectionType];

export const PrintApiImplementationType = {
  CloudPRNT: 'cloudPRNT',
  DesktopAgent: 'desktopAgent',
  Native: 'native',
  PrintNode: 'printNode',
  ServerDirect: 'serverDirect',
  WebUSB: 'webUsb',
  Weblink: 'weblink',
  WebviewApp: 'webviewApp',
} as const;

export type PrintApiImplementationType = typeof PrintApiImplementationType[keyof typeof PrintApiImplementationType];

export const PrintApiPrinterType = {
  receipt: 'receipt',
  label: 'label',
} as const;

export type PrintApiPrinterType = typeof PrintApiPrinterType[keyof typeof PrintApiPrinterType];

export const PrintApiLanguage = {
  epson: 'epson',
  starPRNT: 'starPRNT',
  starGraphic: 'starGraphic',
  starLine: 'starLine',
  zpl: 'zpl',
  legacyEsc: 'legacyEsc',
  raw: 'raw',
} as const;

export type PrintApiLanguage = typeof PrintApiLanguage[keyof typeof PrintApiLanguage];

export const PrintApiJobStatus = {
  pending: 'pending',
  processing: 'processing',
  completed: 'completed',
} as const;

export type PrintApiJobStatus = typeof PrintApiJobStatus[keyof typeof PrintApiJobStatus];

export type PrintApiPaginatedQueryParams = {
  limit?: number | string; // max 500
  page?: number | string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export type PrintApiQueryResponse<T> = {
  items: T[];
  limit: number;
  page: number;
  total: number;
  totalPages: number;
};

export type PrintApiJobStatusEntity = PrintApiEntity & {
  jobId: string;
  stage: PrintApiJobStatus;
}

export type PrintApiJobCreateParams = {
  base64Content?: string;
  printerId: string;
  markdown?: string;
  cutPaper?: boolean;
  popDrawer?: boolean;
  copies?: number;
  status?: PrintApiJobStatus;
}

export type PrintApiJobUpdateParams = {
  error?: string | null;
  status: PrintApiJobStatus;
};

export type PrintApiJob = PrintApiEntity & {
  markdown?: string;
  base64Content?: string;

  printerId: string;
  title: string;
  r2Path: string;
  cutPaper: boolean;
  popDrawer: boolean;
  copies: number;
  status: PrintApiJobStatus;
  statusHistory: PrintApiJobStatusEntity[];
  error?: string;
  printer?: PrintApiPrinter;
  /**
   * Best-effort, point-in-time read of the printer's live connection state at job creation/update
   * time (see `LocationDurableObject.isPrinterOnline` in the `print` repo). Only present on
   * responses from `POST /jobs`, `POST /jobs/:id/reprint`, and `PUT /jobs/:id` - optional/absent
   * elsewhere (e.g. older cached print-service deploys), so callers must treat `undefined` as
   * "unknown", not "online".
   */
  printerOnline?: boolean;
}

export type PrintApiPrinterQueryParams = PrintApiPaginatedQueryParams & {
  language?: PrintApiLanguage;
  type?: PrintApiPrinterType;
  name?: string;
  implementationType?: PrintApiImplementationType;
  connectionType?: PrintApiConnectionType;
  macAddress?: string;
  sourceId?: string | string[];
  enabled?: boolean | string;
}

export type PrintApiPrinterCreateParams = {
  name: string;
  connectionType: PrintApiConnectionType;
  implementationType: PrintApiImplementationType;
  language: PrintApiLanguage;
  type: PrintApiPrinterType;
  sourceId?: string;
  enabled?: boolean;
  metadata?: {
    vendorId?: number;
    productId?: number;
    ipAddress?: string;
    macAddress?: string;
    serialNumber?: string;
    model?: string;
    custom?: Record<string, unknown>;
  };
};

export type PrintApiSyncParams = {
  printers: PrintApiPrinterCreateParams[];
};

export type PrintApiSyncResult = {
  created: PrintApiPrinter[];
  updated: PrintApiPrinter[];
  errored: PrintApiPrinterCreateParams[];
};

export type PrintApiPrinter = PrintApiEntity & {
  connectionType: PrintApiConnectionType;
  implementationType: PrintApiImplementationType;
  language: PrintApiLanguage;
  type: PrintApiPrinterType;
  name: string;
  metadata: {
    vendorId: number;
    productId: number;
    ipAddress: string;
    macAddress: string | null;
    serialNumber: string | null;
    model: string | null;
    custom: Record<string, unknown> | null;
  };
};


